import React, { useEffect } from 'react';
import { Container, Card, VStack, Button, Text, Input, Textarea } from '@chakra-ui/react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toaster } from '@/components/ui/toaster';
import { Field } from '@/components/ui/field';
import api from '@/utils/api';
import { AIContentRequest } from '@/lib/types';

const formSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().min(1, 'Description is required'),
  ai_prompt_content: z.string().optional(),
  ai_prompt_image: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

const EditAIContentRequest: React.FC = () => {
  const { workspaceId, contentTypeId, id } = useParams<{
    workspaceId: string;
    contentTypeId: string;
    id: string;
  }>();
  const navigate = useNavigate();

  const {
    reset,
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      description: '',
      ai_prompt_content: '',
      ai_prompt_image: '',
    },
  });

  const [request, setRequest] = React.useState<AIContentRequest | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);

  useEffect(() => {
    const fetchRequest = async () => {
      try {
        const response = await api.get(`/ai-content-requests/${id}`);
        const requestData = response.data.data;

        setRequest(requestData);
        // Reset form with fetched data
        const formData = {
          name: requestData.name || '',
          description: requestData.description || '',
          ai_prompt_content: requestData.ai_prompt_content || '',
          ai_prompt_image: requestData.ai_prompt_image || '',
        };

        reset(formData);
      } catch (err: any) {
        toaster.create({
          type: 'error',
          title: err.response?.data?.error || 'Failed to load AI content request',
        });
        navigate(`/${workspaceId}/content-types/${contentTypeId}/ai-content-requests`);
      } finally {
        setIsLoading(false);
      }
    };

    if (id) {
      fetchRequest();
    }
  }, [id, reset, navigate, workspaceId, contentTypeId]);

  const onSubmit = async (data: FormValues) => {
    if (!workspaceId || !contentTypeId || !id) {
      return;
    }

    try {
      const payload = {
        ...request,
        name: data.name,
        description: data.description,
        ai_prompt_content: data.ai_prompt_content,
        ai_prompt_image: data.ai_prompt_image,
      }

      await api.put(`/ai-content-requests/${id}`, payload);
      toaster.create({
        type: 'success',
        title: 'AI Content Request updated successfully!',
      });
      navigate(`/${workspaceId}/content-types/${contentTypeId}/ai-content-requests`);
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to update AI content request',
      });
    }
  };

  const handleCancel = () => {
    navigate(`/${workspaceId}/content-types/${contentTypeId}/ai-content-requests`);
  };

  if (isLoading) {
    return (
      <Card.Root minH={'100vh'} mx={'auto'} width={{ md: '90%' }} maxW={'6xl'}>
        <Card.Body>
          <Container maxW={'3xl'}>
            <Text>Loading...</Text>
          </Container>
        </Card.Body>
      </Card.Root>
    );
  }

  if (!request) {
    return (
      <Card.Root minH={'100vh'} mx={'auto'} width={{ md: '90%' }} maxW={'6xl'}>
        <Card.Body>
          <Container maxW={'3xl'}>
            <Text>AI Content Request not found</Text>
          </Container>
        </Card.Body>
      </Card.Root>
    );
  }

  return (
    <Card.Root minH={'100vh'} mx={'auto'} width={{ md: '90%' }} maxW={'6xl'}>
      <Card.Header textAlign={'center'}>
        <Card.Title fontSize={'3xl'}>Edit AI Content Request</Card.Title>
      </Card.Header>
      <Card.Body>
        <Container maxW={'3xl'}>
          {request && (
            <form onSubmit={handleSubmit(onSubmit)}>
              <VStack gap={6} align={'flex-start'}>
                <Field
                  label='Name'
                  invalid={!!errors.name}
                  errorText={errors.name?.message}
                >
                  <Input {...register('name')} defaultValue={request.name} />
                </Field>

                <Field
                  label='Description'
                  invalid={!!errors.description}
                  errorText={errors.description?.message}
                >
                  <Textarea {...register('description')} rows={4} defaultValue={request.description} />
                </Field>

                <Field
                  label='AI Prompt Content'
                  invalid={!!errors.ai_prompt_content}
                  errorText={errors.ai_prompt_content?.message}
                >
                  <Textarea {...register('ai_prompt_content')} rows={4} defaultValue={request.ai_prompt_content || ''} placeholder='Enter the AI prompt for content...' />
                </Field>

                <Field
                  label='AI Prompt Image'
                  invalid={!!errors.ai_prompt_image}
                  errorText={errors.ai_prompt_image?.message}
                >
                  <Textarea {...register('ai_prompt_image')} rows={4} defaultValue={request.ai_prompt_image || ''} placeholder='Enter the AI prompt for image...' />
                </Field>

                <VStack gap={3} width={'full'}>
                  <Button
                    colorPalette='brand'
                    type={'submit'}
                    width={'full'}
                    loading={isSubmitting}
                    loadingText='Saving...'
                  >
                    Save Changes
                  </Button>

                  <Button
                    variant='outline'
                    onClick={handleCancel}
                    width={'full'}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                </VStack>
              </VStack>
            </form>
          )}
        </Container>
      </Card.Body>
    </Card.Root>
  );
};

export default EditAIContentRequest;
